import { IOneViewPermissions } from '@hub-workspace/apps/logged-home/src/app/shared/interfaces/one-view-permissions.interface';

/**
 * Variantes possíveis da experiência OneView (alerta + tooltip do Patrimônio).
 *
 *  - `new`: nova experiência (texto Santander sobre saldo Corretora vs Banco + tooltip).
 *  - `old`: experiência antiga (CTA para o app Santander Corretora).
 */
export type OneViewExperienceVariant = 'new' | 'old';

/**
 * Resolve qual variante da experiência OneView deve ser exibida com base nas
 * permissões do OneView e na condição de cliente Santander (tombado).
 *
 * Matriz de decisão:
 *
 *  | showWalletOneView | isSantander | variante        |
 *  |-------------------|-------------|-----------------|
 *  | false             | *           | null (oculto)   |
 *  | true              | true        | new             |
 *  | true              | false       | old             |
 *
 * Retorna `null` também quando `permissions` ainda não foi carregado.
 */
export function resolveOneViewExperienceVariant(
  permissions: IOneViewPermissions | null | undefined,
  isSantander: boolean,
): OneViewExperienceVariant | null {
  if (!permissions) {
    return null;
  }

  if (!permissions.showWalletOneView) {
    return null;
  }

  return isSantander ? 'new' : 'old';
}

/**
 * Flags que controlam a exibição de cada variante da experiência OneView.
 */
export interface OneViewExperienceFlags {
  /** Habilita alerta `new` + tooltip do Patrimônio para tombados. */
  santanderEnabled: boolean;
  /** Habilita alerta `old` para não-tombados. */
  genericEnabled: boolean;
}

/**
 * Aplica o gate das feature flags sobre a variante resolvida. Cada variante
 * só sobrevive se a flag correspondente estiver habilitada; caso contrário,
 * retorna `null` (nenhuma experiência é exibida).
 */
export function applyOneViewExperienceFlags(
  variant: OneViewExperienceVariant | null,
  flags: OneViewExperienceFlags,
): OneViewExperienceVariant | null {
  if (variant === 'new') {
    return flags.santanderEnabled ? 'new' : null;
  }
  if (variant === 'old') {
    return flags.genericEnabled ? 'old' : null;
  }
  return null;
}
